import React from 'react';

import { Stack } from '@gogame/components';
import DataGrid from '@gogame/data-grid';
import { useChildComponent } from '@gogame/hooks-ts';
import { ScreenLink, useScreenState, useScreenDispatch } from '@gogame/screens';

import AdminUserActions from 'scenes/Users/AdminUsers/components/Actions';
import {
  UserFormDuplicate,
  UserFormEdit,
} from 'scenes/Users/AdminUsers/components/UserForm';
import {
  COLUMN_ID,
  COLUMN_NAME,
  COLUMN_POSITION,
  COLUMN_MANAGER,
  COLUMN_DEPARTMENT,
  COLUMN_LAST_LOGIN,
  COLUMN_REG_DATE,
  COLUMN_COMPANY,
  COLUMN_STUDIO,
  COLUMN_TEAM,
  COLUMN_SUPER_TEAM,
  COLUMN_TEST,
} from 'scenes/Users/AdminUsers/constants';
import { useLoadAdminUsers } from 'scenes/Users/AdminUsers/data';
import { adminUsersDetailsLink } from 'scenes/Users/AdminUsers/navigation';
import {
  SET_TABLE_SETTINGS,
  REORDER_COLUMNS,
  COLLAPSE_COLUMNS,
} from 'scenes/Users/AdminUsers/screenReducer';

import SelectedRowsToolbarActions from './SelectedRowsToolbarActions';
import Toolbar from './Toolbar';

const UserDataTable = () => {
  const [childComponent, openChildComponent] = useChildComponent();
  const screenState = useScreenState();
  const dispatch = useScreenDispatch();

  const { data, isLoading, isRefetching } = useLoadAdminUsers({
    filters: screenState.filters,
    tableSettings: screenState.tableSettings,
    searchQuery: screenState.searchQuery,
  });

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleDuplicate = ({ id }) => {
    openChildComponent(UserFormDuplicate, {
      id,
    });
  };

  const handleEdit = ({ id }) => {
    openChildComponent(UserFormEdit, {
      id,
    });
  };

  return (
    <React.Fragment>
      {childComponent}
      <DataGrid
        selectedRowsToolbarActions={({ selectedValues }) => (
          <SelectedRowsToolbarActions
            selectedValues={selectedValues}
            openChildComponent={openChildComponent}
          />
        )}
        checkboxSelection={true}
        rows={data?.rows}
        count={data?.count}
        loading={isLoading}
        fetching={isRefetching}
        ready={screenState.ready}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={<Toolbar />}
        storageKey={'USERS_LOCAL_STORAGE'}
        defaultColumnWidth={160}
        filtered={!!screenState.searchQuery}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          width={90}
          fixed={true}
          resizable={false}
          align={'right'}
          headerAlign={'right'}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          headerName={'Full Name'}
          width={200}
          fixed={true}
          resizable={false}
          renderCell={({ row: { id, name, email } }) => {
            return (
              <Stack
                spacing={0.5}
                justifyContent={'center'}
                sx={{ height: '100%', lineHeight: 'normal' }}
              >
                <ScreenLink link={adminUsersDetailsLink} pathParams={{ id }}>
                  {name}
                </ScreenLink>
                <span>{email}</span>
              </Stack>
            );
          }}
        />
        <DataGrid.Column
          field={COLUMN_COMPANY}
          type={'text'}
          headerName={'Company'}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_STUDIO}
          type={'text'}
          headerName={'Studio'}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_DEPARTMENT}
          type={'text'}
          headerName={'Department'}
          width={200}
        />
        <DataGrid.Column
          field={COLUMN_TEAM}
          type={'text'}
          headerName={'Team'}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_SUPER_TEAM}
          type={'text'}
          headerName={'Super Team'}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_MANAGER}
          type={'text'}
          headerName={'Manager'}
        />
        <DataGrid.Column
          field={COLUMN_POSITION}
          type={'text'}
          headerName={'Position'}
        />
        <DataGrid.Column
          field={COLUMN_TEST}
          headerName={'Test'}
          type={'bool'}
          align={'center'}
        />
        <DataGrid.Column
          field={COLUMN_LAST_LOGIN}
          type={'date'}
          headerName={'Last Login'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          field={COLUMN_REG_DATE}
          type={'date'}
          headerName={'Created'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          type={'actions'}
          actionsColDef
          limit={3}
          actionsComponent={AdminUserActions}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
        />
      </DataGrid>
    </React.Fragment>
  );
};

export default UserDataTable;
